import "@finance/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@finance/context/AuthContext";
import { ThemeProvider } from "@finance/context/ThemeContext";
import { Toaster } from "@finance/components/ui/sonner";
import Profile from "@finance/pages/Profile";
import ExportBackup from "@finance/pages/ExportBackup";
import DayBook from "@finance/pages/DayBook";
import Login from "@finance/pages/Login";
import Dashboard from "@finance/pages/Dashboard";
import MyActions from "@finance/pages/MyActions";
import AllActions from "@finance/pages/AllActions";
import Transactions from "@finance/pages/Transactions";
import Payables from "@finance/pages/Payables";
import DocumentInbox from "@finance/pages/DocumentInbox";
import Clients from "@finance/pages/Clients";
import Suppliers from "@finance/pages/Suppliers";
import Executive from "@finance/pages/Executive";
import TaxSales from "@finance/pages/TaxSales";
import Others from "@finance/pages/Others";
import Reports from "@finance/pages/Reports";
import Forex from "@finance/pages/Forex";
import VatReconciliation from "@finance/pages/VatReconciliation";
import BankRec from "@finance/pages/BankRec";
import CashPosition from "@finance/pages/CashPosition";
import CashConfirm from "@finance/pages/CashConfirm";
import CardsRecon from "@finance/pages/CardsRecon";
import Periods from "@finance/pages/Periods";
import OpeningBalances from "@finance/pages/OpeningBalances";
import GeneralLedger from "@finance/pages/GeneralLedger";
import Manual from "@finance/pages/Manual";
import DeletedItems from "@finance/pages/DeletedItems";
import Inventory from "@finance/pages/Inventory";
import ItemHistory from "@finance/pages/ItemHistory";
import Aging from "@finance/pages/Aging";
import Expenses from "@finance/pages/Expenses";
import ExpensesV3 from "@finance/pages/ExpensesV3";
import ExpenseReports from "@finance/pages/ExpenseReports";
import Payroll from "@finance/pages/Payroll";
import Items from "@finance/pages/Items";
import Currencies from "@finance/pages/Currencies";
import Allocations from "@finance/pages/Allocations";
import EndToEnd from "@finance/pages/EndToEnd";
import Disclaimers from "@finance/pages/Disclaimers";
import ClientPOs from "@finance/pages/ClientPOs";
import OpsRequests from "@finance/pages/OpsRequests";
import Statements from "@finance/pages/Statements";
import TaskEmails from "@finance/pages/TaskEmails";
import AssetRegister from "@finance/pages/AssetRegister";
import LoanRegister from "@finance/pages/LoanRegister";
import HR from "@finance/pages/HR";
import AssetLodgment from "@finance/pages/AssetLodgment";
import LodgmentHistory from "@finance/pages/LodgmentHistory";
import AdvancesRegister from "@finance/pages/AdvancesRegister";
import CashRequests from "@finance/pages/CashRequests";
import DocumentNumbering from "@finance/pages/DocumentNumbering";
import StatementSchedule from "@finance/pages/StatementSchedule";
import ClientInvoices from "@finance/pages/ClientInvoices";
import AuthorizationLog from "@finance/pages/AuthorizationLog";
import UserAccess from "@finance/pages/UserAccess";
import ResetPassword from "@finance/pages/ResetPassword";
import AdminProcedures from "@finance/pages/AdminProcedures";
import PoHealth from "@finance/pages/PoHealth";
import MarginAudit from "@finance/pages/MarginAudit";
import InvoiceIntegrity from "@finance/pages/InvoiceIntegrity";
import GpVariance from "@finance/pages/GpVariance";
import PendingAdjustments from "@finance/pages/PendingAdjustments";
import PaymentSchedule from "@finance/pages/PaymentSchedule";
import EfdPending from "@finance/pages/EfdPending";
import ProcurementEfd from "@finance/pages/ProcurementEfd";
import Shipments from "@finance/pages/Shipments";
import FulfillmentReports from "@finance/pages/FulfillmentReports";
import TripReports from "@finance/pages/TripReports";
import FinancialReports from "@finance/pages/FinancialReports";
import Wht from "@finance/pages/Wht";
import Compliance from "@finance/pages/Compliance";
import DocumentGallery from "@finance/pages/DocumentGallery";
import FinanceCalendar from "@finance/pages/FinanceCalendar";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="flex h-screen items-center justify-center text-sm text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role_code === "HR" && !location.pathname.startsWith("/hr") && location.pathname !== "/admin-procedures" && location.pathname !== "/reports/trips") return <Navigate to="/hr/employee" replace />;
  if (user.role_code === "AP" && location.pathname.startsWith("/payroll")) return <Navigate to="/" replace />;
  if (user.role_code === "documentation" && !location.pathname.startsWith("/reports/fulfillment") && location.pathname !== "/profile") return <Navigate to="/reports/fulfillment?tab=pending-signed-grv" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/my-actions" element={<Protected><MyActions /></Protected>} />
      <Route path="/all-actions" element={<Protected><AllActions /></Protected>} />
      <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
      <Route path="/transactions/:id" element={<Protected><Transactions /></Protected>} />
      <Route path="/client-invoices" element={<Protected><ClientInvoices /></Protected>} />
      <Route path="/authorization-log" element={<Protected><AuthorizationLog /></Protected>} />
      <Route path="/payables" element={<Protected><Payables /></Protected>} />
      <Route path="/payment-schedule" element={<Protected><PaymentSchedule /></Protected>} />
      <Route path="/reports/expense-efd" element={<Protected><EfdPending /></Protected>} />
      <Route path="/reports/procurement-efd" element={<Protected><ProcurementEfd /></Protected>} />
      <Route path="/expenses" element={<Protected><ExpensesV3 /></Protected>} />
      <Route path="/expenses-legacy" element={<Protected><Expenses /></Protected>} />
      <Route path="/expense-reports" element={<Protected><ExpenseReports /></Protected>} />
      <Route path="/asset-lodgment" element={<Protected><AssetLodgment /></Protected>} />
      <Route path="/asset-lodgments" element={<Protected><LodgmentHistory /></Protected>} />
      <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
      <Route path="/allocations" element={<Protected><Allocations /></Protected>} />
      <Route path="/advances" element={<Protected><AdvancesRegister /></Protected>} />
      <Route path="/cash-requests" element={<Protected><CashRequests /></Protected>} />
      <Route path="/bank-balances" element={<Navigate to="/cash-position" replace />} />
      <Route path="/items" element={<Protected><Items /></Protected>} />
      <Route path="/admin-procedures" element={<Protected><AdminProcedures /></Protected>} />
      <Route path="/currencies" element={<Protected><Currencies /></Protected>} />
      <Route path="/reports/end-to-end" element={<Navigate to="/reports/end-to-end/local" replace />} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/reports/vat-reconciliation" element={<Protected><VatReconciliation /></Protected>} />
      <Route path="/reports/forex" element={<Protected><Forex /></Protected>} />
      <Route path="/reports/fulfillment" element={<Protected><FulfillmentReports /></Protected>} />
      <Route path="/reports/trips" element={<Protected><TripReports /></Protected>} />
      <Route path="/reports/financials" element={<Protected><FinancialReports /></Protected>} />
      <Route path="/wht" element={<Protected><Wht /></Protected>} />
      <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
      <Route path="/document-gallery" element={<Protected><DocumentGallery /></Protected>} />
      <Route path="/finance-calendar" element={<Protected><FinanceCalendar /></Protected>} />
      <Route path="/reports/po-health" element={<Protected><PoHealth /></Protected>} />
      <Route path="/reports/margin-audit" element={<Protected><MarginAudit /></Protected>} />
      <Route path="/reports/invoice-integrity" element={<Protected><InvoiceIntegrity /></Protected>} />
      <Route path="/reports/gp-variance" element={<Protected><GpVariance /></Protected>} />
      <Route path="/reports/pending-adjustments" element={<Protected><PendingAdjustments /></Protected>} />
      <Route path="/reports/end-to-end/local" element={<Protected><EndToEnd only="local" /></Protected>} />
      <Route path="/reports/end-to-end/import" element={<Protected><EndToEnd only="import" /></Protected>} />
      <Route path="/disclaimers" element={<Protected><Disclaimers /></Protected>} />
      <Route path="/client-pos" element={<Protected><ClientPOs /></Protected>} />
      <Route path="/ops-requests" element={<Protected><OpsRequests /></Protected>} />
      <Route path="/client-pos/:id" element={<Protected><ClientPOs /></Protected>} />
      <Route path="/vat" element={<Protected><TaxSales /></Protected>} />
      <Route path="/others" element={<Protected><Others /></Protected>} />
      <Route path="/bank" element={<Protected><BankRec /></Protected>} />
      <Route path="/cash-position" element={<Protected><CashPosition /></Protected>} />
      <Route path="/cash-confirmations" element={<Protected><CashConfirm /></Protected>} />
      <Route path="/cards" element={<Protected><CardsRecon /></Protected>} />
      <Route path="/periods" element={<Protected><Periods /></Protected>} />
      <Route path="/opening-balances" element={<Protected><OpeningBalances /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/export" element={<Protected><ExportBackup /></Protected>} />
      <Route path="/daybook" element={<Protected><DayBook /></Protected>} />
      <Route path="/gl" element={<Protected><GeneralLedger /></Protected>} />
      <Route path="/statements" element={<Protected><Statements /></Protected>} />
      <Route path="/reports/task-emails" element={<Protected><TaskEmails /></Protected>} />
      <Route path="/assets" element={<Protected><AssetRegister /></Protected>} />
      <Route path="/loans" element={<Protected><LoanRegister /></Protected>} />
      <Route path="/hr" element={<Protected><HR /></Protected>} />
      <Route path="/hr/:module" element={<Protected><HR /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/inventory/item/:sku" element={<Protected><ItemHistory /></Protected>} />
      <Route path="/shipments" element={<Protected><Shipments /></Protected>} />
      <Route path="/aging" element={<Protected><Aging /></Protected>} />
      <Route path="/documents" element={<Protected><DocumentInbox /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/suppliers" element={<Protected><Suppliers /></Protected>} />
      <Route path="/manual" element={<Protected><Manual /></Protected>} />
      <Route path="/deleted" element={<Protected><DeletedItems /></Protected>} />
      <Route path="/executive" element={<Protected><Executive /></Protected>} />
      <Route path="/settings/document-numbering" element={<Protected><DocumentNumbering /></Protected>} />
      <Route path="/settings/statement-schedule" element={<Protected><StatementSchedule /></Protected>} />
      <Route path="/settings/users" element={<Protected><UserAccess /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter basename="/finance">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}
